import { FunctionComponent, useEffect, useState } from "react";
import { Button, Card, Container, Group, Stack, Table, Text, TextInput, Select, Space, Tabs, Switch, Collapse, ActionIcon } from "@mantine/core";
import TopNavbarAdmin from "src/components/TopNavbarAdmin";
import { StyledContainer } from "src/components/common/StyledComponents/Container";
import { StyledContainerInset } from "src/components/common/StyledComponents/BackgroundContainer";
import { FocusButton } from "src/components/common/Buttons/FocusButton";
import { useAppDispatch } from "src/redux/redux-store/hooks";
import { COLORS, FONT_SIZES } from "src/utils/styles";
import { paymentService } from "src/services/services/payment/payment.api";
import { CouponWithPromoCodes, Product } from "src/interfaces/Payments";
import { ResponseKind } from "src/services/services/MainService";
import useResponsive from "src/hooks/useResponsive";
import Icon from "src/components/common/Icons/Icons/Icon";
import { usePaymentManagement } from 'src/hooks/usePaymentManagement';
import { CreatePriceModal } from "src/components/Modals/CreatePriceModal";
import { CreateCouponModal } from "src/components/Modals/CreateCouponModal";
import useAdminSettings from "src/hooks/useAdminSettings";

const AdminPricingAndCoupons: FunctionComponent = () => {
    const {backgroundColor, backgroundColorExterior, isMobile} = useResponsive()
    const { prices, coupons, products, fetchPrices, fetchCoupons, fetchProducts,setPrices } = usePaymentManagement();
    const { adminSettings, updatePaymentConfig } = useAdminSettings();
    const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
    const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("prices");
    const [expandedProducts, setExpandedProducts] = useState<string[]>([]);
    const [expandedCoupons, setExpandedCoupons] = useState<string[]>([]);
    const [newPromoCode, setNewPromoCode] = useState({ couponId: "", code: "" });
    console.log("Coupons", coupons)
    console.log("Admin Settings", adminSettings)

    const createPrice = async (values: { productId: string; unitAmount: string; billingCycle: string }) => {
        await paymentService.createPriceForProduct({
            product_type: "subscription",
            product_id: values.productId,
            unit_amount: Number(values.unitAmount),
            billing_cycle: values.billingCycle as "monthly" | "yearly",
        });
        fetchPrices();
        setIsPriceModalOpen(false);
    };

    const createCoupon = async (values: { name: string; percentOff?: number; amountOff?: number }) => {
        await paymentService.createCoupon({
            name: values.name,
            percent_off: values.percentOff ? Number(values.percentOff) : undefined,
            amount_off: values.amountOff ? Number(values.amountOff) * 100 : undefined,
        });
        fetchCoupons();
        setIsCouponModalOpen(false);
    };

    const togglePrice = async (priceId: string, active: boolean, index: number) => {
        const response = await paymentService.togglePriceStatus(priceId, active);
        if(response.kind == ResponseKind.OK){
            setPrices((prevPrices) => {
                const newPrices = [...prevPrices];
                newPrices[index].prices = newPrices[index].prices.map(price => price.price_id === priceId ? { ...price, active: active } : price);
                return newPrices;
            });
        }
    };

    const togglePromoCode = async (promoCodeId: string, active: boolean) => {
        if (active) {
            await paymentService.activatePromoCode(promoCodeId);
        } else {
            await paymentService.deactivatePromoCode(promoCodeId);
        }
        fetchCoupons();
    };

    const createPromoCode = async (couponId: string) => {
        await paymentService.createPromoCode({
            coupon_id: couponId,
            code: newPromoCode.code,
        });
        fetchCoupons();
        setNewPromoCode({ couponId: "", code: "" });
    };

    useEffect(() => {
        fetchPrices();
        fetchCoupons();
        fetchProducts();
    }, []);

    const setDefaultPromoCode = async (promoCodeId: string, promoCode: string) => {
         updatePaymentConfig(promoCode, promoCodeId)
    };

    const setDefaultPrice = async (priceId: string, productId: string) => {
        const response = await paymentService.setDefaultPrice(priceId, productId);
        if (response.kind === ResponseKind.OK) {
            fetchPrices();
        }
    };

    return (
        <StyledContainer theme={{ backgroundColor: backgroundColorExterior }}>
            <StyledContainerInset theme={{ backgroundColor: backgroundColor }}>
                <div className="m-4 h-screen overflow-y-auto">
                    {/* <Text size={FONT_SIZES.h1} fw={600} mb="xl">
                        Admin Pricing and Coupons
                    </Text> */}

                    <Tabs value={activeTab} onTabChange={(value: string) => setActiveTab(value)}>
                        <Tabs.List>
                            <Tabs.Tab value="prices">Prices</Tabs.Tab>
                            <Tabs.Tab value="coupons">Coupons</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="prices">
                            <Stack my="md">
                                <Group position="apart">
                                    <Text size="lg" fw={500}>Prices</Text>
                                    <Button 
                                        radius="xl"
                                        onClick={() => setIsPriceModalOpen(true)} 
                                        rightIcon={<Icon name="plus" />}
                                    >
                                        Create New Price
                                    </Button>
                                </Group>
                                <Card withBorder>
                                    {prices.map((product, index) => (
                                        <div key={product.product_id}>
                                            <Group p="md" position="apart">
                                                <Group>
                                                    <ActionIcon
                                                        onClick={() => {
                                                            setExpandedProducts(
                                                                expandedProducts.includes(product.product_id)
                                                                    ? expandedProducts.filter(id => id !== product.product_id)
                                                                    : [...expandedProducts, product.product_id]
                                                            );
                                                        }}
                                                    >
                                                        {expandedProducts.includes(product.product_id) 
                                                            ? <Icon name="up" />
                                                            : <Icon name="down" />}
                                                    </ActionIcon>
                                                    <Text fw={500}>{product.product_name}</Text>
                                                </Group>
                                            </Group>
                                            
                                            <Collapse in={expandedProducts.includes(product.product_id)}>
                                                <Table>
                                                    <thead>
                                                        <tr>
                                                            <th>Price</th>
                                                            <th>Billing Cycle</th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                            <th>Set Default</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {product.prices.map((price: any) => (
                                                            <tr key={price.price_id}>
                                                                <td>
                                                                    <Group spacing="xs">
                                                                        ${price.unit_amount}
                                                                        {price.is_default_price && (
                                                                            <Text size="xs" color="dimmed">
                                                                                (Default)
                                                                            </Text>
                                                                        )}
                                                                    </Group>
                                                                </td>
                                                                <td>{price.billing_interval}</td>
                                                                <td>{price.active ? "Active" : "Inactive"}</td>
                                                                <td>
                                                                    <Switch
                                                                        checked={price.active}
                                                                        onChange={(event) => 
                                                                            togglePrice(price.price_id, event.currentTarget.checked, index)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Button
                                                                        size="xs"
                                                                        variant="light"
                                                                        disabled={!price.active || price.is_default_price}
                                                                        onClick={() => setDefaultPrice(price.price_id, product.product_id)}
                                                                    >
                                                                        Make Default
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Collapse>
                                        </div>
                                    ))}
                                </Card>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value="coupons">
                            <Stack my="md">
                                <Group position="apart">
                                    <Text size="lg" fw={500}>Coupons</Text>
                                    <Button 
                                        radius="xl"
                                        onClick={() => setIsCouponModalOpen(true)} 
                                        rightIcon={<Icon name="plus" />}
                                    >
                                        Create New Coupon
                                    </Button>
                                </Group>
                                <Card withBorder>
                                    {coupons.map((coupon) => (
                                        <div key={coupon.coupon_id}>
                                            <Group p="md" position="apart">
                                                <Group>
                                                    <ActionIcon
                                                        onClick={() => {
                                                            setExpandedCoupons(
                                                                expandedCoupons.includes(coupon.coupon_id)
                                                                    ? expandedCoupons.filter(id => id !== coupon.coupon_id)
                                                                    : [...expandedCoupons, coupon.coupon_id]
                                                            );
                                                        }}
                                                    >
                                                        {expandedCoupons.includes(coupon.coupon_id)
                                                            ? <Icon name="up" />
                                                            : <Icon name="down" />}
                                                    </ActionIcon>
                                                    <Text fw={500}>{coupon.name}</Text>
                                                </Group>
                                                <Text color="dimmed">
                                                    {coupon.percent_off
                                                        ? `${coupon.percent_off}% Off`
                                                        : `$${(coupon.amount_off || 0) / 100} Off`}
                                                </Text>
                                            </Group>

                                            <Collapse in={expandedCoupons.includes(coupon.coupon_id)}>
                                                <Card withBorder m="md">
                                                    <form onSubmit={(e) => {
                                                        e.preventDefault();
                                                        if (newPromoCode.code) {
                                                            createPromoCode(coupon.coupon_id);
                                                        }
                                                    }}>
                                                        <Group>
                                                            <TextInput
                                                                placeholder="Enter promo code"
                                                                value={newPromoCode.couponId === coupon.coupon_id ? newPromoCode.code : ""}
                                                                error={!/^[a-zA-Z0-9-]+$/.test(newPromoCode.code) && newPromoCode.code !== "" ? "Only letters, numbers, and hyphens allowed" : ""}
                                                                onChange={(e) => {
                                                                    const value = e.target.value.toUpperCase();
                                                                    if (value === '' || /^[a-zA-Z0-9-]+$/.test(value)) {
                                                                        setNewPromoCode({
                                                                            couponId: coupon.coupon_id,
                                                                            code: value
                                                                        });
                                                                    }
                                                                }}
                                                                styles={{ input: { textTransform: 'uppercase' } }}
                                                            />
                                                            <Button
                                                                type="submit"
                                                                rightIcon={<Icon name="plus" />}
                                                                disabled={!newPromoCode.code || !/^[a-zA-Z0-9-]+$/.test(newPromoCode.code)}
                                                            >
                                                                Add Promo Code
                                                            </Button>
                                                        </Group>
                                                    </form>
                                                </Card>

                                                <Table>
                                                    <thead>
                                                        <tr>
                                                            <th>Code</th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                            <th>Default</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {coupon.promo_codes.map((promo) => (
                                                            <tr key={promo.promo_code_id}>
                                                                <td>{promo.code}</td>
                                                                <td>{promo.active ? "Active" : "Inactive"}</td>
                                                                <td>
                                                                    <Switch
                                                                        checked={promo.active}
                                                                        onChange={(event) =>
                                                                            togglePromoCode(promo.promo_code_id, event.currentTarget.checked)
                                                                        }
                                                                    />
                                                                  
                                                                </td>
                                                                <td>
                                                                    <Switch
                                                                        checked={adminSettings?.promo_code?.promo_code_id === promo.promo_code_id}
                                                                        onChange={(event) =>
                                                                            setDefaultPromoCode(promo.promo_code_id, promo.code)
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Collapse>
                                        </div>
                                    ))}
                                </Card>
                            </Stack>
                        </Tabs.Panel>
                    </Tabs>
                </div>
            </StyledContainerInset>

            <CreatePriceModal
                opened={isPriceModalOpen}
                onClose={() => setIsPriceModalOpen(false)}
                onSubmit={(values) => createPrice(values)}

                products={products}
            />
            
            <CreateCouponModal
                opened={isCouponModalOpen}
                onClose={() => setIsCouponModalOpen(false)}
                onSubmit={(values) => createCoupon(values)}
            />
        </StyledContainer>
    );
};

export default AdminPricingAndCoupons;
